import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Layout,
  Typography,
  Button,
  Dropdown,
  Space,
  Avatar,
  Badge,
  Popover,
} from "antd";
import { LogoutOutlined, UserOutlined, LockOutlined } from "@ant-design/icons";
import notificationIcon from "../../assets/icon-header/Notification.svg";
import notificationActiveIcon from "../../assets/icon-header/Notification-active.svg";
import menuIcon from "../../assets/icon-header/Menu.svg";
import { useAuth } from "../../contexts/AuthContext";
import { useAvatar } from "../../contexts/AvatarContext";
// Note: Header sử dụng avatarUri từ AvatarContext, context này đã load avatar theo pattern chuẩn
// (buildFullUrl + fetchImageAsDataUri) từ user profile, nên không cần load lại ở đây
import { useNavigate, useLocation } from "react-router-dom";
import type { MenuProps } from "antd";
import { Notifications } from "./Notifications";
import { ProfileSidebar } from "./ProfileSidebar";
import { LogoutConfirmModal } from "./LogoutConfirmModal";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { SearchInput } from "./SearchInput";
import { notificationService } from "../../services/notification.service";
import { useNotificationSocketHeader } from "../../hooks/useNotificationSocketHeader";
import styles from "./Header.module.css";

const { Header: AntHeader } = Layout;
const { Text } = Typography;

// Mapping từ route path sang page title
const getPageTitle = (pathname: string): string | null => {
  if (pathname.includes("/personal/profile")) {
    return "Hồ sơ cá nhân";
  }
  if (pathname.includes("/management/personnel")) {
    return "Quản lý nhân sự";
  }
  if (pathname.includes("/management/role-permissions")) {
    return "Quản lý quyền hạn và vai trò";
  }
  if (pathname.includes("/management/department")) {
    return "Quản lý phòng ban";
  }
  if (pathname.includes("/management/timekeeping")) {
    return "Quản lý chấm công";
  }
  if (pathname.includes("/management/salary/timesheet-table")) {
    return "Bảng chấm công";
  }
  if (pathname.includes("/management/activity-logs")) {
    return "Nhật ký hoạt động hệ thống";
  }
  if (pathname.includes("/management/settings")) {
    return "Cài đặt hệ thống";
  }
  return null;
};

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { avatarUri } = useAvatar();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationRefreshTrigger, setNotificationRefreshTrigger] =
    useState(0);
  const [profileSidebarVisible, setProfileSidebarVisible] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pageTitle = getPageTitle(location.pathname);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setLogoutLoading(false);
      setLogoutModalOpen(false);
    }
  };

  const handleLogoutClick = () => {
    setLogoutModalOpen(true);
  };

  const handleChangePasswordClick = () => {
    setChangePasswordModalOpen(true);
  };

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationService.getNotifications({
        limit: 100,
      });
      if (response.success && response.data) {
        const unread = response.data.filter((n) => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      // Silent fail
    }
  }, []);

  // Socket.IO listeners cho real-time unread count updates
  const { isConnected, socket } = useNotificationSocketHeader({
    onNewNotification: () => fetchUnreadCount(),
    onNotificationRead: () => fetchUnreadCount(),
    onRefresh: () => fetchUnreadCount(),
  });

  // Polling cho unread count (chỉ khi không có socket)
  useEffect(() => {
    fetchUnreadCount();

    // Chỉ poll nếu không có socket hoặc socket chưa kết nối và tab visible
    if ((!socket || !isConnected) && !document.hidden) {
      intervalRef.current = setInterval(() => {
        if (!document.hidden) {
          fetchUnreadCount();
        }
      }, 30000);
      return () => {
        intervalRef.current && clearInterval(intervalRef.current);
      };
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [fetchUnreadCount, socket, isConnected]);

  // Refresh khi tab/window được focus lại (với debounce để tránh spam)
  useEffect(() => {
    let focusTimeout: ReturnType<typeof setTimeout> | null = null;
    let visibilityTimeout: ReturnType<typeof setTimeout> | null = null;

    const debouncedFetch = () => {
      if (!document.hidden) {
        fetchUnreadCount();
      }
    };

    const handleFocus = () => {
      if (focusTimeout) clearTimeout(focusTimeout);
      focusTimeout = setTimeout(debouncedFetch, 1000);
    };

    const handleVisibilityChange = () => {
      if (visibilityTimeout) clearTimeout(visibilityTimeout);
      if (!document.hidden) {
        visibilityTimeout = setTimeout(debouncedFetch, 1000);
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      focusTimeout && clearTimeout(focusTimeout);
      visibilityTimeout && clearTimeout(visibilityTimeout);
    };
  }, [fetchUnreadCount]);

  const items: MenuProps["items"] = [
    {
      key: "profile",
      label: (
        <div className={styles.menuItem}>
          <Text strong style={{ fontSize: "16px" }}>
            {user?.name}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "14px" }}>
            {user?.email}
          </Text>
        </div>
      ),
      disabled: true,
    },
    {
      type: "divider",
    },
    {
      key: "changePassword",
      label: "Đổi mật khẩu",
      icon: <LockOutlined />,
      onClick: handleChangePasswordClick,
    },
    {
      key: "logout",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogoutClick,
    },
  ];

  return (
    <AntHeader className={styles.header}>
      {pageTitle && (
        <div className={styles.leftSection}>
          <h1 className={styles.pageTitle}>{pageTitle}</h1>
        </div>
      )}
      <div className={styles.rightSection}>
        <Space size={8} align="center">
          <SearchInput />

          <Popover
            content={
              <Notifications
                onClose={() => setNotificationOpen(false)}
                onMarkAsRead={fetchUnreadCount}
                refreshTrigger={notificationRefreshTrigger}
              />
            }
            title={null}
            trigger="click"
            open={notificationOpen}
            onOpenChange={(open) => {
              setNotificationOpen(open);
              if (open) {
                fetchUnreadCount();
                setNotificationRefreshTrigger((prev) => prev + 1);
              }
            }}
            placement="bottomRight"
          >
            <Badge count={unreadCount} size="small" offset={[-2, 4]}>
              <Button
                type="text"
                shape="circle"
                className={styles.iconButton}
                onClick={() => setNotificationOpen(!notificationOpen)}
              >
                <img
                  src={
                    notificationOpen ? notificationActiveIcon : notificationIcon
                  }
                  alt="Notifications"
                  className={styles.notificationIcon}
                />
              </Button>
            </Badge>
          </Popover>

          <Button
            type="default"
            className={styles.userButton}
            onClick={() => setProfileSidebarVisible(true)}
          >
            <span>{user?.name || "User"}</span>
            <Avatar
              size={32}
              src={avatarUri?.trim() ? avatarUri : undefined}
              icon={!avatarUri?.trim() ? <UserOutlined /> : undefined}
              onError={() => false}
            />
          </Button>

          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" shape="circle" className={styles.iconButton}>
              <img src={menuIcon} alt="Menu" className={styles.menuIcon} />
            </Button>
          </Dropdown>
        </Space>
      </div>
      <ProfileSidebar
        visible={profileSidebarVisible}
        onClose={() => setProfileSidebarVisible(false)}
      />
      <LogoutConfirmModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleLogout}
        loading={logoutLoading}
      />
      <ChangePasswordModal
        open={changePasswordModalOpen}
        onClose={() => setChangePasswordModalOpen(false)}
      />
    </AntHeader>
  );
};
